import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import { FINANCE_ROLES, CORE_STAFF_ROLES } from '../../../config/roleConfig';
import ownerReportApi from '../../../services/ownerReportApi';
import { formatBusinessMonth } from '../../../utils/dateFormatter';
import MonthlyReportsList from './MonthlyReportsList';
import MonthlyReportView from './MonthlyReportView';
import '../../../styles/MonthlyReports.css';

/**
 * "التقارير الشهرية" — the monthly owner report section on /finance.
 *
 * One surface, two audiences. The role split here is presentation only; the real
 * boundary is server-side (an owner's list simply cannot contain an unreleased or
 * failed report, and their payload arrives as an allow-list). This component never
 * filters for access.
 *
 * ── Empty state ──────────────────────────────────────────────────────────────
 * Ops always sees the section, with an Arabic empty state — they need somewhere to
 * look when a run should have produced something. An OWNER sees nothing until they
 * have at least one released report: an empty box for a feature that is not live
 * yet reads as "where is my report?", a question someone then answers by hand.
 *
 * Reads the persisted snapshot only. There is no "preview live" path, by design.
 */

const MonthlyReportsSection = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // Two levels, mirroring the route gates. Seeing every owner's figures is a
  // finance-visibility question; deciding what an owner receives is not — and
  // FINANCE_ROLES includes maintenance-supervisor. Frontend gate is cosmetic; the
  // backend refuses the writes regardless.
  const isOps = FINANCE_ROLES.includes(user?.role);
  const canManage = CORE_STAFF_ROLES.includes(user?.role);

  const [reports, setReports] = useState([]);
  const [automation, setAutomation] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await ownerReportApi.getReports();
      setReports(res?.data?.reports || []);
      // Ops-only, and absent on an owner's response — the empty state below reads
      // it rather than asserting a schedule that may not be switched on.
      setAutomation(res?.data?.automation || null);
    } catch (err) {
      setError('فشل في تحميل التقارير الشهرية');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);

  const openReport = useCallback(async (reportId) => {
    try {
      setBusyId(reportId);
      setError(null);
      const res = await ownerReportApi.getReport(reportId);
      setSelected(res?.data?.report || null);
    } catch (err) {
      setError('فشل في تحميل التقرير');
    } finally {
      setBusyId(null);
    }
  }, []);

  const releaseReport = useCallback(async (reportId) => {
    try {
      setBusyId(reportId);
      setError(null);
      await ownerReportApi.releaseReport(reportId);
    } catch (err) {
      // `preserveBackendError` is set on this call, so the backend's specific
      // reason survives (e.g. a failed snapshot cannot be released).
      setError(err?.response?.data?.error || 'فشل في إظهار التقرير للمالك');
    } finally {
      // Reload REGARDLESS of outcome — see regenerateReport below for why.
      await loadReports();
      setBusyId(null);
    }
  }, [loadReports]);

  const regenerateReport = useCallback(async (report) => {
    // Regenerating clears the release flag — say so BEFORE the click takes effect,
    // not after. window.confirm is the established pattern in this codebase.
    const warning = report.released
      ? `إعادة إنشاء تقرير ${formatBusinessMonth(report.periodMonth)} تُخفيه عن المالك — سيحتاج إلى مراجعة وإظهار من جديد. المتابعة؟`
      : `إعادة إنشاء تقرير ${formatBusinessMonth(report.periodMonth)}؟`;

    if (!window.confirm(warning)) return;

    const ownerId = report.owner?._id || report.owner;

    try {
      setBusyId(report._id);
      setError(null);
      await ownerReportApi.regenerateReport(ownerId, report.periodMonth);
    } catch (err) {
      setError(err?.response?.data?.error || 'فشل في إنشاء التقرير');
    } finally {
      // Reload REGARDLESS of outcome. A regenerate that times out client-side may
      // still have committed on the server — and it clears `released` when it does.
      // Refreshing only on success would leave the list asserting a release state
      // that no longer exists, which is worse than the error message itself.
      await loadReports();
      setBusyId(null);
    }
  }, [loadReports]);

  // Owners see no section at all until a report has been released to them — and nothing while
  // that is still being checked, or every owner would see the section flash (step 7).
  if (!isOps && reports.length === 0 && (loading || !error)) return null;

  return (
    <div className="monthly-reports-section" dir="rtl">
      <div className="section-card">
        <div className="section-header">
          <h3>
            <i className="bi bi-file-earmark-bar-graph" aria-hidden="true"></i>
            التقارير الشهرية
          </h3>
          <p className="section-subtitle">
            {isOps
              ? 'أداء كل مالك شهرياً — يُراجع ثم يُظهر للمالك'
              : 'أداء عقاراتك شهرياً'}
          </p>
        </div>

        {error && (
          <div className="report-error" role="alert">
            <i className="bi bi-exclamation-triangle" aria-hidden="true"></i>
            {error}
          </div>
        )}

        {loading && (
          <div className="report-loading">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">جاري التحميل...</span>
            </div>
          </div>
        )}

        {/* Ops-only in practice — an owner with no reports never renders the section.
            Reads the server's own flag rather than hardcoding a cadence: promising
            automation that is switched off leaves ops waiting for a report nobody is
            producing, which is the one thing this copy must never do. */}
        {/* Never under an error: the empty text would contradict it, and it describes how ops
            produce reports — internal copy an owner must not see (step 7). */}
        {!loading && !error && reports.length === 0 && (
          <p className="report-empty">
            {automation?.enabled
              ? `لا توجد تقارير شهرية بعد. تُنشأ التقارير تلقائياً يوم ${automation.dayOfMonth} من كل شهر، بعد إغلاق الشهر السابق.`
              : 'لا توجد تقارير شهرية بعد. تُنشأ التقارير حالياً يدوياً بعد إغلاق الشهر.'}
          </p>
        )}

        {!loading && reports.length > 0 && !selected && (
          <MonthlyReportsList
            reports={reports}
            isOps={isOps}
            canManage={canManage}
            onOpen={openReport}
            onRelease={releaseReport}
            onRegenerate={regenerateReport}
            busyId={busyId}
          />
        )}

        {selected && (
          <>
            <div className="report-view-actions">
              <button
                type="button"
                className="btn-report-action report-back"
                onClick={() => setSelected(null)}
              >
                <i className="bi bi-arrow-right" aria-hidden="true"></i>
                رجوع إلى قائمة التقارير
              </button>

              {/* Prints the DOCUMENT, not this page. Printing from here would carry
                  the whole dashboard onto paper — the print view renders the report
                  alone, branded, with the methodology panel open. */}
              <button
                type="button"
                className="btn-report-action btn-report-print"
                onClick={() => navigate(`/finance/reports/${selected._id}/print?auto=1`)}
              >
                <i className="bi bi-printer" aria-hidden="true"></i>
                طباعة التقرير
              </button>
            </div>
            <MonthlyReportView report={selected} />
          </>
        )}
      </div>
    </div>
  );
};

export default MonthlyReportsSection;
